import React, { useCallback, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { AnimatePresence } from "framer-motion";
import {
  Box,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingBox from "../indicators/LoadingBox";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { LocationMarker } from "../map/markers/LocationMarker";
import { useDispatch, useSelector } from "react-redux";
import {
  createOrderMarker,
  MapCenterUpdater,
  SetDragPosition,
} from "../../utils/map";
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { getShippingRoutes } from "../../api/shipping";
import { setCenter } from "../../store/actions/mapSlice";
import { setShowOrderInfosModal } from "../../store/actions/ordersSlice";
import { checkDeliveryCenterRegister } from "../../utils/deliveryCenter";
import { useHistory } from "react-router-dom";

// Import the new modal components
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../modals/AnimatedModal";

// Icons
import PrintIcon from "@mui/icons-material/Print";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MapIcon from "@mui/icons-material/Map";
import ShippingRouteDetailsTicket from "./ShippingRouteDetailsTicket";
import { printShippingRoute } from "../../utils/printService";

// Map view component
const MapView = ({ mapCenter, deliveryCenter, orders }) => (
  <Box
    sx={{
      height: "100%",
      width: "100%",
      borderRadius: 2,
      overflow: "hidden",
      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    }}
  >
    <MapContainer
      center={[mapCenter.lat, mapCenter.lng]}
      zoom={13}
      scrollWheelZoom={true}
      zoomControl={true}
      style={{ height: "100%", width: "100%" }}
    >
      <TileLayer
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <LocationMarker
        centerLat={deliveryCenter?.lat}
        centerLng={deliveryCenter?.lng}
      />

      {orders.map((order) => {
        const { id, fk_order } = order;
        const { lat, lng } = fk_order;
        const icon = createOrderMarker(fk_order, "onWay");

        return (
          <Marker key={id} position={[lat, lng]} icon={icon}>
            <Popup>
              <Typography variant="body2" fontWeight="500">
                Pedido {fk_order.order_index}
              </Typography>
              <Typography variant="body2">{fk_order.address}</Typography>
            </Popup>
          </Marker>
        );
      })}

      <SetDragPosition />
      <MapCenterUpdater />
    </MapContainer>
  </Box>
);

// Modernized ShippingRouteShowModal component using the new componentized structure
export default function ShippingRouteShowModal({
  show,
  closeModal,
  shippingRouteId,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Create ref for the printable content
  const componentRef = useRef(null);
  const [ticketHeightInMM, setTicketHeightInMM] = useState(0);
  const [ticketWidthInMM, setTicketWidthInMM] = useState(0);

  // State for tabbed interface on mobile
  const [activeTab, setActiveTab] = useState(0);

  // Separate state to track if data is ready to print
  const [printReady, setPrintReady] = useState(false);

  const deliveryCenter = useSelector(
    (state) => state.deliveryCenter.deliveryCenter
  );

  const [loading, setLoading] = useState(false);
  const [shippingRoute, setShippingRoute] = useState(null);
  const [orders, setOrders] = useState([]);

  const mapCenter = useSelector((state) => state.map.center);
  const showOrderInfosModal = useSelector(
    (state) => state.orders.showOrderInfosModal
  );

  useEffect(() => {
    if (shippingRoute && componentRef.current) {
      setTicketSize();

      const timer = setTimeout(() => {
        setPrintReady(true);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setPrintReady(false);
    }
  }, [shippingRoute, componentRef]);

  const setTicketSize = () => {
    const boundingRect = componentRef.current.getBoundingClientRect();

    // Calculating height
    const height = boundingRect.height;
    const pixelsPerMM = 96 / 25.4; // Standard pixel density
    const heightInMM = height / pixelsPerMM;
    setTicketHeightInMM(heightInMM);

    // Calculating width
    const width = boundingRect.width;
    const widthInMM = width / pixelsPerMM;
    setTicketWidthInMM(widthInMM);
  };

  const handlePrint = async () => {
    try {
      await printShippingRoute({ route: shippingRoute });
    } catch (e) {
      // Fallback to react-to-print
      reactToPrintTrigger();
    }
  };

  const reactToPrintTrigger = useReactToPrint({
    contentRef: componentRef,
    onPrintError: (errorLocation, error) => {
      console.error(`Print error at ${errorLocation}:`, error);
      dispatchSnackBarMessage(
        `print-error-${Date.now()}`,
        `Erro ao imprimir: ${error.message}`,
        "error"
      );
    },
    pageStyle: `
      @media print {
        @page {
          margin: 0;
          size: auto ${ticketHeightInMM}mm;
        }
        @top-left { content: none; }
        @top-center { content: none; }
        @top-right { content: none; }
        @bottom-left { content: none; }
        @bottom-center { content: none; }
        @bottom-right { content: none; }
      }
    `,
    onAfterPrint: () => console.log("Shipping Printed"),
  });

  useEffect(() => {
    if (!shippingRouteId || !show) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setPrintReady(false);

        if (!deliveryCenter) {
          await checkDeliveryCenterRegister(history);
        }

        if (deliveryCenter) {
          handleChangeMapCenter(deliveryCenter?.lat, deliveryCenter?.lng);
        }

        const params = {
          srid: shippingRouteId,
        };
        const { data } = await getShippingRoutes(params);
        setShippingRoute(data);
        setOrders(data.shipping_routes_orders || []);
        setLoading(false);
      } catch (e) {
        console.error(e);
        dispatchSnackBarMessage(
          `shipping-route-detail-${Date.now()}`,
          "Erro ao carregar entrega",
          "error"
        );
        setLoading(false);
      }
    };

    loadData();
  }, [show, shippingRouteId]);

  const handleChangeMapCenter = useCallback(
    (lat, lng) => {
      const latNumber = parseFloat(lat);
      const lngNumber = parseFloat(lng);

      dispatch(
        setCenter({
          lat: latNumber,
          lng: lngNumber,
        })
      );
    },
    [dispatch]
  );

  const handleOrderInfoModal = (order_id) => {
    dispatch(
      setShowOrderInfosModal({
        orderId: order_id,
        show: !showOrderInfosModal,
      })
    );
  };

  // Define custom header actions
  const headerActions = [
    <IconButton
      key="print-button"
      onClick={handlePrint}
      disabled={!printReady}
      color="primary"
      sx={{ mr: 1 }}
      aria-label="Imprimir"
      title="Imprimir relatório"
    >
      <PrintIcon />
    </IconButton>,
  ];

  if (loading) {
    return (
      <AnimatedModal isOpen={show} onClose={closeModal}>
        <Box
          sx={{
            p: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingBox />
        </Box>
      </AnimatedModal>
    );
  }

  return (
    <AnimatePresence>
      <AnimatedModal isOpen={show} onClose={closeModal} maxHeight="90vh">
        {/* Use the ModalHeader component */}
        <ModalHeader
          title="Dados da Entrega"
          icon={LocalShippingIcon}
          onClose={closeModal}
          actions={headerActions}
        />

        {/* Mobile tabs */}
        {isMobile && (
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab icon={<MapIcon />} label="Mapa" />
            <Tab icon={<ListAltIcon />} label="Detalhes" />
          </Tabs>
        )}

        {/* Use the ModalContent component */}
        <ModalContent padding={3}>
          {isMobile ? (
            // Mobile layout with tabs
            <>
              <Box
                sx={{
                  display: activeTab === 0 ? "block" : "none",
                  height: "calc(70vh - 116px)",
                }}
              >
                <MapView
                  mapCenter={mapCenter}
                  deliveryCenter={deliveryCenter}
                  orders={orders}
                />
              </Box>

              {/* Mobile details tab */}
              <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
                <ShippingRouteDetailsTicket
                  shippingRoute={shippingRoute}
                  componentRef={componentRef}
                  onOrderClick={handleOrderInfoModal}
                />
              </Box>
            </>
          ) : (
            // Desktop layout with grid
            <Grid container spacing={3} sx={{ height: "calc(70vh)" }}>
              <Grid item xs={12} md={8} sx={{ height: "100%" }}>
                <MapView
                  mapCenter={mapCenter}
                  deliveryCenter={deliveryCenter}
                  orders={orders}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ShippingRouteDetailsTicket
                  shippingRoute={shippingRoute}
                  componentRef={componentRef}
                  onOrderClick={handleOrderInfoModal}
                />
              </Grid>
            </Grid>
          )}
        </ModalContent>
      </AnimatedModal>
    </AnimatePresence>
  );
}
